/**
 * Secret Vault Page.
 *
 * Tabs:
 *   - My Secrets
 *   - Shared With Me
 *   - Shared By Me  (secrets I own that have at least one share)
 *
 * Security rules (see docs/design/user-secret-vault-architecture.md §6.3):
 *   1. No endpoint returns the encrypted value, so we never show it.
 *   2. All identifiers in selectors use email/name — never UUID.
 *   3. Rotate / revoke are explicit actions with a confirmation modal.
 */

import { useState } from 'react';
import { Eye, History, Key, Pencil, RefreshCw, Share2, Trash2, Plus } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { useI18n } from '@/i18n';
import {
  useDeleteSecret,
  useMySecrets,
  useSharedByMe,
  useSharedWithMe,
} from '../hooks/use-secrets';
import { SecretFormModal } from '../components/SecretFormModal';
import { SecretRotateModal } from '../components/SecretRotateModal';
import { SecretRevealModal } from '../components/SecretRevealModal';
import { SecretShareModal } from '../components/SecretShareModal';
import { SecretVersionHistoryModal } from '../components/SecretVersionHistoryModal';
import type { Secret } from '../types';

type Tab = 'mine' | 'shared-with-me' | 'shared-by-me';

export function SecretVaultPage() {
  const { t } = useI18n();

  const [tab, setTab] = useState<Tab>('mine');
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Secret | null>(null);
  const [rotateTarget, setRotateTarget] = useState<Secret | null>(null);
  const [revealTarget, setRevealTarget] = useState<Secret | null>(null);
  const [shareTarget, setShareTarget] = useState<Secret | null>(null);
  const [historyTarget, setHistoryTarget] = useState<Secret | null>(null);

  const mineQuery = useMySecrets();
  const sharedWithMeQuery = useSharedWithMe();
  const sharedByMeQuery = useSharedByMe();
  const deleteMutation = useDeleteSecret();

  async function handleDelete(secret: Secret) {
    const confirmed = window.confirm(
      t('admin.secrets.delete.confirm', { name: secret.name }) ||
        `Delete secret "${secret.name}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    await deleteMutation.mutateAsync(secret.id);
  }

  function renderTable(secrets: Secret[], emptyMessage: string, showOwnerActions: boolean) {
    if (secrets.length === 0) {
      return (
        <p className="text-sm text-muted-foreground p-4 text-center">{emptyMessage}</p>
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.secrets.table.name') || 'Name'}</TableHead>
            <TableHead>{t('admin.secrets.table.type') || 'Type'}</TableHead>
            <TableHead>{t('admin.secrets.table.expires') || 'Expires'}</TableHead>
            <TableHead>{t('admin.secrets.table.lastUsed') || 'Last used'}</TableHead>
            <TableHead className="text-right">
              {t('admin.secrets.table.actions') || 'Actions'}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secrets.map((secret) => (
            <TableRow key={secret.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{secret.name}</div>
                    {secret.description && (
                      <div className="text-xs text-muted-foreground">
                        {secret.description}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {secret.secret_type
                    ? t(`admin.secrets.types.${secret.secret_type}`) || secret.secret_type
                    : t('admin.secrets.types.generic') || 'generic'}
                </Badge>
              </TableCell>
              <TableCell>
                {secret.expires_at ? (
                  <span className={secret.is_expired ? 'text-destructive' : ''}>
                    {new Date(secret.expires_at).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>
                {secret.last_used_at ? (
                  new Date(secret.last_used_at).toLocaleDateString()
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRevealTarget(secret)}
                    aria-label={t('admin.secrets.actions.reveal') || 'Reveal'}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {showOwnerActions && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditTarget(secret)}
                        aria-label={t('common.edit') || 'Edit'}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRotateTarget(secret)}
                        aria-label={t('admin.secrets.actions.rotate') || 'Rotate'}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryTarget(secret)}
                        aria-label={t('admin.secrets.actions.history') || 'History'}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShareTarget(secret)}
                        aria-label={t('admin.secrets.actions.share') || 'Share'}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(secret)}
                        aria-label={t('common.delete') || 'Delete'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('admin.secrets.title') || 'Secret Vault'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('admin.secrets.subtitle') ||
              'Store credentials and tokens. Share them with users or groups without exposing the value.'}
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.secrets.actions.create') || 'New secret'}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(next) => setTab(next as Tab)}>
        <TabsList>
          <TabsTrigger value="mine">
            {t('admin.secrets.tabs.mine') || 'My Secrets'}
          </TabsTrigger>
          <TabsTrigger value="shared-with-me">
            {t('admin.secrets.tabs.sharedWithMe') || 'Shared With Me'}
          </TabsTrigger>
          <TabsTrigger value="shared-by-me">
            {t('admin.secrets.tabs.sharedByMe') || 'Shared By Me'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mine">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.secrets.tabs.mine') || 'My Secrets'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mineQuery.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (
                renderTable(
                  mineQuery.data?.data ?? [],
                  t('admin.secrets.empty.mine') || 'You have not created any secrets yet.',
                  true,
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shared-with-me">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.secrets.tabs.sharedWithMe') || 'Shared With Me'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sharedWithMeQuery.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (
                renderTable(
                  sharedWithMeQuery.data?.data ?? [],
                  t('admin.secrets.empty.sharedWithMe') ||
                    'No one has shared a secret with you yet.',
                  false,
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shared-by-me">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.secrets.tabs.sharedByMe') || 'Shared By Me'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sharedByMeQuery.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (
                renderTable(
                  sharedByMeQuery.data?.data ?? [],
                  t('admin.secrets.empty.sharedByMe') ||
                    'None of your secrets are shared yet.',
                  true,
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <SecretFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <SecretFormModal
        open={!!editTarget}
        secret={editTarget}
        onClose={() => setEditTarget(null)}
      />
      <SecretRotateModal
        open={!!rotateTarget}
        secret={rotateTarget}
        onClose={() => setRotateTarget(null)}
      />
      <SecretRevealModal
        open={!!revealTarget}
        secret={revealTarget}
        onClose={() => setRevealTarget(null)}
      />
      <SecretShareModal
        open={!!shareTarget}
        secret={shareTarget}
        onClose={() => setShareTarget(null)}
      />
      <SecretVersionHistoryModal
        open={!!historyTarget}
        secret={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </div>
  );
}
